import { useEffect, useRef, useState } from 'react';
import { useReduxState } from 'hooks';
import { scrollToElement } from 'lib/common';
import { getBlockFileForLanguage } from 'lib/courses';
import { waitForVideo } from 'lib/files';
import { Video } from 'components/common';
import { Form, Text } from 'components/common/form';
import { FileUploader } from 'components/common/uploader';
import {
  defaultBlockData,
  EditorBlockCustomControls,
  EditorBlockTranslationReplace
} from 'components/editor/blocks';
import { EditorBlockEmbedFileControls } from 'components/editor/blocks/embed';
import { EditorBlockVideoLinks, EditorBlockVideoSubtitles } from '.';

import type { BlockVideo } from 'shared-types';
import type {
  BlockProps,
  EditorStateProps
} from 'components/editor/blocks/EditorBlock';

type StateProps = Pick<EditorStateProps, 'editorLanguageActive'>;

type Links = BlockVideo['locales'][string]['links'];
type Localizations = BlockVideo['locales'][string]['translatable'];
type Subtitles = BlockVideo['locales'][string]['subtitles'];

const EditorBlockVideo = ({
  blockId,
  data,
  isPreview,
  languageVersion,
  updateData,
  ...stateProps
}: BlockProps<BlockVideo> & StateProps) => {
  const { editorLanguageActive } = stateProps;

  const player = useRef<Plyr>(undefined);

  const {
    messages,
    pageState: { course },
    theme
  } = useReduxState();

  const [fileAdded, setFileAdded] = useState(false);
  const [transcoding, setTranscoding] = useState(false);
  const [videoVisible, setVideoVisible] = useState(false);
  const [showLanguageSpecificUploader, setShowLanguageSpecificUploader] =
    useState(false);

  const {
    activeLanguage,
    shouldShowDefaultFile,
    fileUrl: videoUrl,
    source: videoSource,
    remote_id: videoRemoteId,
    remote_hash: videoRemoteHash
  } = getBlockFileForLanguage({
    data,
    courseLanguage: course.language,
    editorActiveLanguageVersion: editorLanguageActive,
    showLanguageSpecificUploader
  });

  useEffect(() => {
    setTranscoding(false);

    // set videoVisible if there is a video for the current situation
    const showDefaultVideo =
      shouldShowDefaultFile || languageVersion === course.language;

    const locale = showDefaultVideo ? course.language : languageVersion;

    const hasVideoFile =
      data.locales[locale]?.file?.url ||
      (data.locales[locale]?.source && data.locales[locale]?.remote_id);
    if (hasVideoFile) setVideoVisible(true);
    else setVideoVisible(false);
  }, [languageVersion, shouldShowDefaultFile, data.locales]);

  useEffect(() => {
    initVideo(videoUrl);
  }, [videoUrl]);

  useEffect(() => {
    if (videoSource && videoRemoteId) {
      setVideoVisible(true);
      setTranscoding(false);
    }
  }, [videoSource, videoRemoteId]);

  useEffect(() => {
    // when switching language version, reset showLanguageSpecificUploader
    // if the new language version does not have its own file
    const hasLanguageSpecificFile =
      Boolean(data.locales?.[languageVersion]?.file?.url) ||
      (Boolean(data.locales?.[languageVersion]?.source) &&
        Boolean(data.locales?.[languageVersion]?.remote_id));
    if (!hasLanguageSpecificFile) {
      setShowLanguageSpecificUploader(false);
    }
  }, [languageVersion]);

  const initVideo = async (url: string) => {
    if (!url) return;

    if (url.indexOf('/transcoded/') !== -1) {
      await waitForVideo({ onError: () => setTranscoding(true), url });
    }

    setTranscoding(false);
    setVideoVisible(true);
  };

  const getCurrentTime = () => {
    return player.current?.currentTime || 0;
  };

  const handleFileAdded = () => {
    setFileAdded(true);
  };

  const handleLinksChange = (links: Links, localizations: Localizations) => {
    updateData({
      ...data,
      locales: {
        ...data.locales,
        [languageVersion]: {
          ...data.locales[languageVersion],
          ...(updateTranslationsOnly
            ? { translatable: localizations }
            : { links, translatable: localizations })
        }
      }
    });
  };

  const handlePaste = (ev: React.ClipboardEvent<HTMLInputElement>) => {
    const pastedValue = ev.clipboardData.getData('Text');

    const providers = {
      vimeo:
        /(?:http[s]?:\/\/)?(?:www.)?vimeo\.co(?:.+(?:\/)([^/?a-z]*)+(?:[/|?](.*))?$)/,
      youtube:
        /^.*(youtu.*be.*)\/(watch\?v=|embed\/|v|shorts|)(.*?((?=[&#?])|$))/,
      dreambroker:
        /^.*dreambroker.(com|fi)\/channel(\/[a-z0-9]*)(?:\/iframe)?(\/[a-z0-9]*)\??.*$/
    };

    let source = null;
    let remoteId = null;
    let remoteHash = null;
    const providerKeys = Object.keys(providers);
    for (let i = 0; i < providerKeys.length; i++) {
      const match = providers[providerKeys[i]].exec(pastedValue.trim());
      if (
        match &&
        match[1] &&
        match[2] &&
        match[3] &&
        providerKeys[i] == 'dreambroker'
      ) {
        source = 'dreambroker';
        remoteId = match[2] + match[3];
      } else if (
        match &&
        match[1] &&
        match[3] &&
        providerKeys[i] == 'youtube'
      ) {
        source = 'youtube';
        remoteId = match[3];
      } else if (match !== null && match[1] !== undefined) {
        source = providerKeys[i];
        remoteId = match[1];
        if (match[2]) remoteHash = match[2];
        break;
      }
    }
    // no match
    if (!source) return;

    updateData({
      ...data,
      locales: {
        ...data.locales,
        [languageVersion]: {
          ...data.locales[languageVersion],
          source: source,
          remote_id: remoteId,
          remote_hash: remoteHash,
          translatable: {}
        }
      }
    });

    setShowLanguageSpecificUploader(false);
  };

  const handleSubtitleChange = ({ subtitles }: { subtitles: Subtitles }) => {
    updateData({
      ...data,
      locales: {
        ...data.locales,
        [languageVersion]: { ...data.locales[languageVersion], subtitles }
      }
    });
  };

  const handleReplaceCancel = () => {
    setVideoVisible(true);
    setShowLanguageSpecificUploader(false);
  };

  const handleReplaceClick = () => {
    setFileAdded(false);
    setVideoVisible(false);
    setShowLanguageSpecificUploader(true);
    setTimeout(() => {
      const element = document.querySelector(
        `.block[data-block-id="${blockId}"]`
      ) as HTMLElement;
      if (element) {
        const elementTop = element.getBoundingClientRect().top;
        if (elementTop < 0) scrollToElement({ element, offset: 200 });
      }
    }, 100);
  };

  const handleRemoveLanguageVersionSpecificVideo = () => {
    updateData({
      ...data,
      locales: {
        ...data.locales,
        [languageVersion]: {
          ...defaultBlockData.video.locales[languageVersion],
          translatable: {}
        }
      }
    });
  };

  const handleUploadSuccess = ({
    extension,
    url
  }: {
    extension: string;
    url: string;
  }) => {
    let transcodedUrl = extension ? url.slice(0, url.lastIndexOf('.')) : url;
    transcodedUrl = `${transcodedUrl.replace(
      '/original/',
      '/transcoded/'
    )}.mp4`;

    const posterUrl = transcodedUrl
      .replace('/transcoded/', '/thumbnails/')
      .replace('.mp4', '-00001.png');

    updateData({
      ...data,
      locales: {
        ...data.locales,
        [languageVersion]: {
          ...data.locales[languageVersion],
          file: {
            ...(data.locales[languageVersion].file || {}),
            posterUrl: posterUrl,
            url: transcodedUrl
          },
          translatable: {}
        }
      }
    });

    setShowLanguageSpecificUploader(false);
    setTranscoding(true);
    initVideo(transcodedUrl);
  };

  const onVideoReady = () => {
    // dispatch custom event that video is ready
    const eventData = { bubbles: true, detail: { blockId } };
    document.dispatchEvent(new CustomEvent('editor_video_loaded', eventData));
  };

  /**
   * Returns the text label for the video provider based on the video data.
   * @returns The provider label of video as a string, or an empty string if no provider is found.
   */
  const getVideoProviderLabel = (): string => {
    if (data.locales[languageVersion].file?.url) return messages.commonFile;
    if (!data.locales[languageVersion].source) return '';

    switch (data.locales[languageVersion].source) {
      case 'vimeo':
        return 'Vimeo';
      case 'youtube':
        return 'YouTube';
      case 'dreambroker':
        return 'Dream Broker';
      default:
        return '';
    }
  };

  const videoTypeLabel = getVideoProviderLabel();

  const isEditingLanguageVersion = Boolean(editorLanguageActive);

  const updateTranslationsOnly =
    isEditingLanguageVersion && shouldShowDefaultFile;

  const activeLocale = shouldShowDefaultFile
    ? data?.locales?.[course.language]
    : data?.locales?.[activeLanguage || course.language];

  const activeTranslatable = isEditingLanguageVersion
    ? data?.locales?.[languageVersion]?.translatable
    : data?.locales?.[course.language]?.translatable;

  const uploadingVideo = transcoding || showLanguageSpecificUploader;

  const showSubtitles =
    !uploadingVideo &&
    (!updateTranslationsOnly || activeLocale?.subtitles?.length);

  const showLinks =
    !uploadingVideo && (!updateTranslationsOnly || activeLocale?.links?.length);

  const videoBorderRadius =
    !showSubtitles && !showLinks && !editorLanguageActive
      ? theme.cardBorderRadius
      : `${theme.cardBorderRadius} ${theme.cardBorderRadius} 0 0`;

  return (
    <div className="video">
      {transcoding ? (
        <div className="upload-wrapper">
          <FileUploader
            allowedFileTypes={['video/*']}
            autoProceed={true}
            disabled={isPreview}
            dragDrop={!isPreview}
            icon="file-video"
            id={`video-uploader-${blockId}${
              showLanguageSpecificUploader ? '-lang' : ''
            }`}
            inProgress={true}
            inProgressText={messages.uploaderTranscoding}
            keyPrefix="uploads/videos/original/"
            onFileAdded={handleFileAdded}
            onUploadSuccess={handleUploadSuccess}
            placeholder={messages.editorBlockVideoChooseFile}
            useMultipart={true}
            uuidAsFilename={true}
          />
        </div>
      ) : (!videoUrl && !videoSource) || showLanguageSpecificUploader ? (
        <div className="upload-wrapper">
          <FileUploader
            allowedFileTypes={['video/*']}
            autoProceed={true}
            disabled={isPreview}
            dragDrop={!isPreview}
            icon="file-video"
            id={`video-uploader-${blockId}`}
            inProgress={false}
            inProgressText={messages.uploaderTranscoding}
            keyPrefix="uploads/videos/original/"
            onFileAdded={handleFileAdded}
            onUploadSuccess={handleUploadSuccess}
            placeholder={messages.editorBlockVideoChooseFile}
            useMultipart={true}
            uuidAsFilename={true}
          />

          {!fileAdded && !isPreview && (
            <div className="paste-handler">
              <div className="paste-label">
                {messages.editorBlockVideoPasteLabel}
              </div>

              <Form>
                <Text
                  field="videoUrl"
                  id={`video-paste-url-${blockId}`}
                  inputStyle={{ textAlign: 'center' }}
                  onPaste={handlePaste}
                  placeholder={messages.editorBlockVideoPastePlaceholder}
                />
              </Form>
            </div>
          )}
        </div>
      ) : shouldShowDefaultFile ? (
        <div>
          <div style={{ display: videoVisible ? 'block' : 'none' }}>
            {activeLocale?.file?.url ? (
              <Video
                key={activeLanguage + ':' + (activeLocale?.file?.url || '')}
                borderRadius={videoBorderRadius}
                getPlayer={(plyr) => (player.current = plyr)}
                subtitles={activeLocale?.subtitles}
                url={activeLocale?.file?.url}
              />
            ) : videoSource && videoRemoteId ? (
              <Video
                key={activeLanguage + ':' + (videoUrl || videoRemoteId || '')}
                borderRadius={videoBorderRadius}
                getPlayer={(plyr) => (player.current = plyr)}
                provider={videoSource}
                onReady={onVideoReady}
                videoId={videoRemoteId}
                videoHash={videoRemoteHash}
              />
            ) : null}
          </div>
        </div>
      ) : (
        <div style={{ display: videoVisible ? 'block' : 'none' }}>
          {videoUrl ? (
            <Video
              key={activeLanguage + (videoUrl || activeLocale?.file?.url)}
              borderRadius={videoBorderRadius}
              getPlayer={(plyr) => (player.current = plyr)}
              subtitles={activeLocale?.subtitles}
              url={videoUrl}
            />
          ) : videoSource && videoRemoteId ? (
            <Video
              key={activeLanguage + ':' + (videoSource || videoRemoteId || '')}
              borderRadius={videoBorderRadius}
              getPlayer={(plyr) => (player.current = plyr)}
              provider={videoSource}
              onReady={onVideoReady}
              videoId={videoRemoteId}
              videoHash={videoRemoteHash}
            />
          ) : null}
        </div>
      )}

      {videoTypeLabel && !transcoding && !showLanguageSpecificUploader ? (
        <div className="video-type-label">
          {messages.commonType}: {videoTypeLabel}
        </div>
      ) : null}

      {(showSubtitles || showLinks) &&
      (videoUrl || (videoSource && videoRemoteId)) ? (
        <div className="options">
          {videoUrl && showSubtitles ? (
            <EditorBlockVideoSubtitles
              disabled={isPreview || updateTranslationsOnly}
              id={`video-${blockId}`}
              onChange={handleSubtitleChange}
              subtitles={activeLocale?.subtitles}
            />
          ) : null}

          {!isPreview && showLinks ? (
            <EditorBlockVideoLinks
              getCurrentTime={getCurrentTime}
              links={activeLocale?.links || []}
              localizations={activeTranslatable || {}}
              onLinksChange={handleLinksChange}
              translationOnly={updateTranslationsOnly}
              translationReference={
                data?.locales?.[course.language]?.translatable
              }
            />
          ) : null}
        </div>
      ) : null}

      {editorLanguageActive ? (
        <EditorBlockTranslationReplace
          customStatusText={messages.blocksVideoUsingLanguageSpecific}
          defaultStatusText={messages.blocksVideoUsingDefaultLanguage}
          downloadableFileUrl={activeLocale?.file?.url}
          hasCustomValue={!shouldShowDefaultFile}
          icon="play-circle"
          onCancel={handleReplaceCancel}
          onRemove={handleRemoveLanguageVersionSpecificVideo}
          onReplace={handleReplaceClick}
          showCancel={showLanguageSpecificUploader}
        />
      ) : null}

      {!transcoding &&
      !showLanguageSpecificUploader &&
      !shouldShowDefaultFile &&
      videoUrl &&
      !isPreview &&
      !editorLanguageActive ? (
        <EditorBlockCustomControls>
          <EditorBlockEmbedFileControls file={{ url: videoUrl }} type="video" />
        </EditorBlockCustomControls>
      ) : null}

      <style jsx>{`
        .video {
          background-color: ${theme.lightGrey};
          border-radius: ${theme.cardBorderRadius};
        }
        .upload-wrapper {
          background-color: ${theme.lightGrey};
          border-radius: ${theme.cardBorderRadius};
        }
        .paste-handler {
          padding: 5px 15px 15px;
        }
        .paste-label {
          color: #888;
          font-size: 1.4rem;
          font-weight: 600;
          margin-bottom: 5px;
          text-align: center;
        }
        .options {
          padding: 15px;
        }
        .video-type-label {
          color: ${theme.textColorLight};
          font-size: 1.4rem;
          padding: 15px 15px 5px;
        }
      `}</style>
    </div>
  );
};

export default EditorBlockVideo;
